import type { AppData, Island, Scenario, SessionRecord, Settings } from '../types'

/**
 * Offline-first sync.
 *
 * The device is the source of truth and never blocks on the network. Every
 * record carries a logical clock (`updatedAt`); the server stores opaque
 * documents and hands back a monotonic cursor. Merging happens here, on the
 * client, so the server needs no knowledge of what an island is.
 *
 * Resolution is last-write-wins per record, with two deliberate exceptions:
 *
 *   · Practice history is append-only and merges by union. A drill you did on
 *     your phone and a drill you did on your laptop are both facts; neither
 *     should overwrite the other.
 *   · A deletion beats an edit at the same instant, because resurrecting an
 *     island the user deleted is far more annoying than losing an edit they
 *     made to something they then threw away.
 */

export type RecordKind = 'island' | 'scenario' | 'settings' | 'session'

export interface SyncRecord {
  /** `kind:localId`, e.g. `island:isl_work`. Stable across devices. */
  id: string
  kind: RecordKind
  updatedAt: number
  deleted: boolean
  /** Null when deleted. */
  payload: unknown
}

export const SETTINGS_RECORD_ID = 'settings:me'

export function recordId(kind: RecordKind, localId: string): string {
  return `${kind}:${localId}`
}

/** Split `island:isl_work` into its kind and local id. */
export function parseRecordId(id: string): { kind: RecordKind; localId: string } | null {
  const at = id.indexOf(':')
  if (at <= 0) return null
  const kind = id.slice(0, at) as RecordKind
  if (kind !== 'island' && kind !== 'scenario' && kind !== 'settings' && kind !== 'session') {
    return null
  }
  return { kind, localId: id.slice(at + 1) }
}

/** Everything this device believes, as sync records. */
export function toRecords(data: AppData): SyncRecord[] {
  const out: SyncRecord[] = []

  for (const island of data.islands) {
    out.push({
      id: recordId('island', island.id),
      kind: 'island',
      updatedAt: island.updatedAt,
      deleted: false,
      payload: island,
    })
  }

  for (const scenario of data.scenarios) {
    out.push({
      id: recordId('scenario', scenario.id),
      kind: 'scenario',
      updatedAt: scenario.updatedAt,
      deleted: false,
      payload: scenario,
    })
  }

  for (const session of data.history) {
    // Sessions never change after they are written, so `at` is their clock.
    out.push({
      id: recordId('session', session.id),
      kind: 'session',
      updatedAt: session.at,
      deleted: false,
      payload: session,
    })
  }

  out.push({
    id: SETTINGS_RECORD_ID,
    kind: 'settings',
    updatedAt: data.settingsUpdatedAt,
    deleted: false,
    payload: data.settings,
  })

  for (const tomb of data.tombstones) {
    out.push({
      id: tomb.recordId,
      kind: parseRecordId(tomb.recordId)?.kind ?? 'island',
      updatedAt: tomb.deletedAt,
      deleted: true,
      payload: null,
    })
  }

  return out
}

/**
 * Records changed since the last successful push.
 *
 * Dirtiness is derived from the clock rather than tracked in a separate flag,
 * so a crash between mutating and flagging cannot lose a change — the worst
 * case is re-sending a record the server already has, which is idempotent.
 */
export function collectDirty(data: AppData): SyncRecord[] {
  const since = data.sync.lastPushedAt
  const dirty = toRecords(data).filter((r) => r.updatedAt > since)

  /*
   * Settings are withheld until this device has pulled at least once.
   *
   * Every other record is additive — an island, a session, a tombstone — and
   * merges by id. Settings are one record for the whole account, replaced
   * wholesale by the newest clock, so pushing them is a claim to know what the
   * account's preferences are. A device that has never pulled does not.
   *
   * The failure this closes is real and was live: an existing subscriber opens
   * the site in a new browser, drags the speech slider or picks a language —
   * either stamps settingsUpdatedAt with the wall clock on an otherwise
   * factory-default store — then signs in. Sign-in resets lastPushedAt to zero
   * and the sync pushes before it pulls, so those defaults win last-write-wins
   * and wipe the voice, speed, typing mode, tones and levels they had set, on
   * every device they own.
   *
   * Fixing the individual call sites was not enough: there are eight of them
   * across the two apps, several on screens shown before anyone signs in, and
   * any future one would re-open the hole. Withholding the record is the only
   * guard that does not depend on remembering.
   *
   * Nothing is lost by waiting. The record stays dirty — its clock is not in
   * the high-water mark the caller computes from this list — so it goes up on
   * the next cycle, once this device has seen what it would be overwriting.
   */
  if (data.sync.lastSyncAt === null) return dirty.filter((r) => r.kind !== 'settings')
  return dirty
}

function isNewer(incoming: SyncRecord, currentClock: number): boolean {
  if (incoming.updatedAt > currentClock) return true
  // Equal clocks: a delete wins, so deletions are not undone by a same-tick edit.
  return incoming.updatedAt === currentClock && incoming.deleted
}

/**
 * Fold records pulled from the server into local state.
 *
 * Anything the server sends that is older than what we hold is ignored, so a
 * pull can never roll back a newer local edit that has not been pushed yet.
 */
export function applyRecords(
  data: AppData,
  incoming: SyncRecord[],
  now = Date.now(),
): AppData {
  const islands = new Map(data.islands.map((i) => [i.id, i]))
  const scenarios = new Map(data.scenarios.map((s) => [s.id, s]))
  const history = new Map(data.history.map((h) => [h.id, h]))
  const tombstones = new Map(data.tombstones.map((t) => [t.recordId, t]))

  let settings = data.settings
  let settingsUpdatedAt = data.settingsUpdatedAt

  for (const record of incoming) {
    const parsed = parseRecordId(record.id)
    if (!parsed) continue
    const { kind, localId } = parsed

    if (record.deleted) {
      const existing = tombstones.get(record.id)
      if (!existing || record.updatedAt > existing.deletedAt) {
        tombstones.set(record.id, { recordId: record.id, deletedAt: record.updatedAt })
      }
      // Only remove the live record if the deletion is at least as new as it.
      if (kind === 'island') {
        const local = islands.get(localId)
        if (local && isNewer(record, local.updatedAt)) islands.delete(localId)
      } else if (kind === 'scenario') {
        const local = scenarios.get(localId)
        if (local && isNewer(record, local.updatedAt)) scenarios.delete(localId)
      } else if (kind === 'session') {
        history.delete(localId)
      }
      continue
    }

    // A record deleted later than this edit must stay deleted.
    const tomb = tombstones.get(record.id)
    if (tomb && tomb.deletedAt >= record.updatedAt) continue

    switch (kind) {
      case 'island': {
        const local = islands.get(localId)
        if (!local || isNewer(record, local.updatedAt)) {
          islands.set(localId, record.payload as Island)
        }
        break
      }
      case 'scenario': {
        const local = scenarios.get(localId)
        if (!local || isNewer(record, local.updatedAt)) {
          scenarios.set(localId, record.payload as Scenario)
        }
        break
      }
      case 'session': {
        // Append-only: first writer wins, because the record is immutable.
        if (!history.has(localId)) history.set(localId, record.payload as SessionRecord)
        break
      }
      case 'settings': {
        if (record.updatedAt > settingsUpdatedAt) {
          // Which language this site shows is a property of the site, not of
          // the account. Syncing it meant signing in on the Mandarin site
          // pulled down a preference set elsewhere and flipped it to German.
          const remote = record.payload as Settings

          /*
           * The per-language maps are unions, not replacements.
           *
           * Everything else in Settings is one answer for the account and the
           * newest clock should win. These two are five answers each, written
           * one at a time on five separate origins, and taking the incoming
           * object wholesale meant a device whose copy of the map was stale
           * silently erased every language it had not heard about — putting
           * those languages back on the account-wide fallback. That is the
           * "my Mandarin stopped being scheduled" failure again, arriving by a
           * different road.
           *
           * The cost of a union is that an entry can never be removed. Neither
           * map has any way to remove one, so nothing is given up.
           */
          const levelByLanguage = {
            ...data.settings.levelByLanguage,
            ...(remote.levelByLanguage ?? {}),
          }
          const voiceByLanguage = {
            ...data.settings.voiceByLanguage,
            ...(remote.voiceByLanguage ?? {}),
          }
          // A client older than the level setting sends a payload with no level
          // in it at all. Taking that at face value would read as "no answer"
          // and quietly put the learner back to the default.
          const hskLevel = remote.hskLevel ?? data.settings.hskLevel

          settings = {
            ...remote,
            activeLanguage: data.settings.activeLanguage,
            hskLevel,
            levelByLanguage,
            voiceByLanguage,
          }

          /*
           * If anything local survived the merge, this device now holds a
           * version the server has never seen, and it has to be able to say so.
           * Adopting the remote clock would leave the two permanently
           * disagreeing: the push is emitted, the server compares equal clocks,
           * refuses it, and no later action ever repairs it.
           */
          const kept =
            remote.hskLevel === undefined ||
            Object.keys(levelByLanguage).length !== Object.keys(remote.levelByLanguage ?? {}).length ||
            Object.keys(voiceByLanguage).length !== Object.keys(remote.voiceByLanguage ?? {}).length
          settingsUpdatedAt = kept ? Math.max(now, record.updatedAt + 1) : record.updatedAt
        }
        break
      }
    }
  }

  return {
    ...data,
    islands: [...islands.values()],
    scenarios: [...scenarios.values()],
    // Newest first, matching what the dashboard expects.
    history: [...history.values()].sort((a, b) => b.at - a.at),
    settings,
    settingsUpdatedAt,
    tombstones: [...tombstones.values()],
  }
}

/**
 * Drop tombstones that every device has certainly seen.
 *
 * Kept for a long window because a device that has been offline longer than
 * this will resurrect deleted records — 90 days is the trade between unbounded
 * growth and a phone left in a drawer over a season.
 */
export const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000

export function pruneTombstones(data: AppData, now = Date.now()): AppData {
  const cutoff = now - TOMBSTONE_TTL_MS
  const kept = data.tombstones.filter((t) => t.deletedAt > cutoff)
  if (kept.length === data.tombstones.length) return data
  return { ...data, tombstones: kept }
}
